import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AbstractContentComponent } from '../../abstract-content.component';
import { Context } from '@service/context.service';
import { Column } from '@shared/table/column';
import { ChopHierarchyNode, ChopKatalog, ChopMassnahme } from '@entity/chop';
import { AnwenderbetriebService } from '@service/anwenderbetrieb.service';
import { ChopService } from '@service/chop.service';
import { TreeNode } from 'primeng/api';
import { TreeModule } from 'primeng/tree';
import { TabsModule } from 'primeng/tabs';
import { TranslatePipe } from '@ngx-translate/core';
import { TableComponent } from '@shared/table/table.component';
import { ActionButtonDirective } from '@shared/table/action-button.directive';
import { ToolbarButtonDirective } from '@shared/table/toolbar-button.directive';
import { buildChopHierarchyTree } from './chop-hierarchy-tree';
import { BetriebsvariablePickerDialogComponent } from './betriebsvariable-picker-dialog/betriebsvariable-picker-dialog.component';

interface MassnahmeRow {
  tacsCodeKreisId: number;
  tacsId: string;
  bezeichnung: string;
}

interface BetriebsVariableRow {
  id: number;
  tacsCode: string;
  bezeichnung: string;
}

@Component({
  selector: 'app-chop-katalog-detail',
  templateUrl: './chop-katalog-detail.component.html',
  styleUrls: ['./chop-katalog-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TreeModule, TabsModule, TranslatePipe, TableComponent, ActionButtonDirective, ToolbarButtonDirective],
})
export class ChopKatalogDetailComponent extends AbstractContentComponent implements OnInit {
  private anwenderbetriebService = inject(AnwenderbetriebService);
  private chopService = inject(ChopService);

  private katalogId = Number(this.activatedRoute.snapshot.params.katalogId);

  treeNodes: TreeNode[] = [];
  selectedProfil: ChopHierarchyNode | null = null;

  activeTab = 'massnahmen';
  massnahmenRows: MassnahmeRow[] = [];
  massnahmenColumns: Column[] = [];

  betriebsVariableRows: BetriebsVariableRow[] = [];
  betriebsVariableColumns: Column[] = [];

  private hierarchyNodes: ChopHierarchyNode[] = [];
  private massnahmenById = new Map<number, ChopMassnahme>();
  private katalog?: ChopKatalog;

  constructor() {
    super(Context.Anwenderbetrieb);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setColumns();
    this.loadHierarchy();
  }

  protected onLanguageChange(): void {
    this.setColumns();
    this.treeNodes = buildChopHierarchyTree(this.hierarchyNodes, this.getLanguageCaseSensitive());
    this.refreshBreadcrumbs();
    this.massnahmenRows = this.massnahmenRows.map((r) => this.buildMassnahmeRow(r.tacsCodeKreisId));
    this.markForCheck();
  }

  onNodeSelect(event: { node: TreeNode }): void {
    const node = event.node.data as ChopHierarchyNode;
    if (node.type !== 'profil') {
      this.selectedProfil = null;
      return;
    }
    this.selectedProfil = node;
    this.loadProfilData(node.id);
  }

  showAddBetriebsVariableDialog(): void {
    const abId = this.getAnwenderbetriebId();
    if (!abId || !this.selectedProfil) {
      return;
    }
    const dialogRef = this.dialogService.open(BetriebsvariablePickerDialogComponent, {
      modal: true,
      closable: true,
      focusOnShow: false,
      width: '700px',
      header: this.translate('CHOP_KATALOGE.ADD_BETRIEBSVARIABLE'),
      data: {
        anwenderbetriebId: abId,
        profilId: this.selectedProfil.id,
        alreadyAssignedIds: this.betriebsVariableRows.map((r) => r.id),
      },
    });
    const onClose = dialogRef?.onClose.subscribe((result) => {
      if (result) {
        this.reloadBetriebsVariablen();
      }
    });
    if (onClose) {
      this.subscriptions.push(onClose);
    }
  }

  removeBetriebsVariable(row: BetriebsVariableRow): void {
    const abId = this.getAnwenderbetriebId();
    if (!abId || !this.selectedProfil) {
      return;
    }
    const profilId = this.selectedProfil.id;
    this.confirm({
      header: this.translate('CHOP_KATALOGE.REMOVE_BV_HEADER'),
      message: this.translate('CHOP_KATALOGE.REMOVE_BV_MESSAGE'),
      accept: () => {
        this.subscriptions.push(
          this.anwenderbetriebService.removeChopProfilVariable(abId, profilId, row.id).subscribe({
            next: () => {
              this.showSuccessMessage();
              this.reloadBetriebsVariablen();
            },
            error: (err) => this.showErrorMessage(undefined, err?.error?.message),
          })
        );
      },
    });
  }

  private loadHierarchy(): void {
    const abId = this.getAnwenderbetriebId();
    if (!abId) {
      return;
    }
    this.subscriptions.push(
      this.anwenderbetriebService.getChopKataloge(abId).subscribe((kataloge) => {
        this.katalog = kataloge.find((k) => k.id === this.katalogId);
        this.refreshBreadcrumbs();
        if (this.katalog) {
          this.loadAllMassnahmen(this.katalog.jahr);
        }
      })
    );
    this.subscriptions.push(
      this.anwenderbetriebService.getChopHierarchie(abId, this.katalogId).subscribe((nodes) => {
        this.hierarchyNodes = nodes;
        this.treeNodes = buildChopHierarchyTree(nodes, this.getLanguageCaseSensitive());
        this.markForCheck();
      })
    );
  }

  private loadAllMassnahmen(jahr: number): void {
    this.subscriptions.push(
      this.chopService.getMassnahmen(jahr).subscribe((massnahmen) => {
        this.massnahmenById = new Map(massnahmen.map((m) => [m.id, m]));
      })
    );
  }

  private loadProfilData(profilId: number): void {
    this.subscriptions.push(
      this.chopService.getProfilMassnahmen(profilId).subscribe((profilMassnahmen) => {
        this.massnahmenRows = profilMassnahmen.map((a) => this.buildMassnahmeRow(a.tacsCodeKreisId));
        this.markForCheck();
      })
    );
    this.reloadBetriebsVariablen();
  }

  private reloadBetriebsVariablen(): void {
    const abId = this.getAnwenderbetriebId();
    if (!abId || !this.selectedProfil) {
      return;
    }
    this.subscriptions.push(
      this.anwenderbetriebService.getChopProfilVariablen(abId, this.selectedProfil.id).subscribe((variablen) => {
        this.betriebsVariableRows = variablen.map((v) => ({
          id: v.id,
          tacsCode: v.tacsCode,
          bezeichnung: v.bezeichnung,
        }));
        this.markForCheck();
      })
    );
  }

  private buildMassnahmeRow(tacsCodeKreisId: number): MassnahmeRow {
    const massnahme = this.massnahmenById.get(tacsCodeKreisId);
    return {
      tacsCodeKreisId,
      tacsId: massnahme?.tacsId ?? '',
      bezeichnung: massnahme ? this.getLocalizedText(massnahme.bezeichnung) : '',
    };
  }

  private refreshBreadcrumbs(): void {
    const katalogLabel = this.katalog ? `${this.katalog.jahr} ${this.getLocalizedText(this.katalog.bezeichnung)}` : '';
    this.setBreadcrumbs([
      { label: this.translate('CHOP_KATALOGE.TITLE'), routerLink: ['/chop-kataloge'] },
      { label: katalogLabel },
    ]);
  }

  private setColumns(): void {
    this.massnahmenColumns = [
      { field: 'tacsId', headerKey: 'CHOP.CODE', width: '200px' },
      { field: 'bezeichnung', headerKey: 'CHOP.BEZEICHNUNG', minWidth: '300px' },
    ];
    this.betriebsVariableColumns = [
      { field: 'tacsCode', headerKey: 'CHOP.CODE', width: '200px' },
      { field: 'bezeichnung', headerKey: 'CHOP.BEZEICHNUNG', minWidth: '300px' },
    ];
  }
}
